import { useEffect, useRef } from 'react'
import { hasActiveSubscription } from 'Model/User/IUser.js'
import type { IUser } from 'Model/User/IUser.js'
import { isOrganizationOverrideActive } from 'Model/User/organizationOverride.js'
import { tracingAxios } from 'Repository/Sentry.js'
import { Environment } from 'environment.js'

interface UseSessionSyncOptions {
  user: IUser | undefined
  isCheckoutReturn: boolean
  isRechargeReturn: boolean
  syncing: boolean
  setSyncing: (value: boolean) => void
}

/**
 * Syncs the Keycloak session once on mount when the user has no active
 * subscription and we are not in a checkout-return flow.
 * The X-Token-Refreshed response header triggers updateUserIfPresent() via
 * the axios interceptor which updates Redux state and causes fetchData to re-run.
 */
export function useSessionSync({ user, isCheckoutReturn, isRechargeReturn, syncing, setSyncing }: UseSessionSyncOptions): void {
  const sessionSynced = useRef(false)

  // sync on mount for users without an active subscription
  useEffect(() => {
    if (sessionSynced.current || isCheckoutReturn || isRechargeReturn) return
    if (isOrganizationOverrideActive(user)) return
    if (user && !hasActiveSubscription(user.org)) {
      sessionSynced.current = true
      setSyncing(true)
      tracingAxios
        .get(`${Environment.API_URL}/v1/chargebee/refresh-session`, { withCredentials: true })
        .then(() => {
          setTimeout(() => setSyncing(false), 500)
        })
        .catch((e) => {
          console.warn('Session sync failed', e)
          setSyncing(false)
        })
    }
  }, [user, isCheckoutReturn, isRechargeReturn])

  // stop syncing immediately when subscription data arrives
  useEffect(() => {
    if (syncing && user && hasActiveSubscription(user.org)) {
      setSyncing(false)
    }
  }, [syncing, user?.org.chargebeePlanId, user?.org.chargebeeSubscriptionStatus])

  // don't block the page forever if sync stalls
  useEffect(() => {
    if (!syncing) return
    const timeout = setTimeout(() => setSyncing(false), 5000)
    return () => clearTimeout(timeout)
  }, [syncing])
}
